import { Link } from '@tanstack/react-router';
import { useLingui } from '@lingui/react/macro';
import { ArrowUpRight } from 'lucide-react';
import { useRef, useState, type ReactNode } from 'react';
import { motion, useScroll, useSpring, useTransform, useReducedMotion, type MotionValue } from 'motion/react';
import { cn } from '@worldcoo/ui/lib/utils';
import { Image } from '@/routes/-components/image';
import { Eyebrow, Squiggle } from '../primitives';

type Solution = { badge: string; chip: string; title: string; description: string; photo: string; src: string };

// Card + peeking-tab geometry, shared by the sticky offset and the layout so they can never drift.
// `top-i = STACK_TOP + i * TAB` pins each card exactly one tab-height below the previous, so the
// full-width tab (number + label) of every card underneath stays visible as the deck piles up.
const STACK_TOP = 6; // rem — clears the sticky nav (~5.9rem) with a hair of breathing room
const TAB = 4; // rem — height of the peeking tab (matches h-16)

// Depth feel — as cards pile on top of a buried one it recedes: a touch smaller and darker per
// layer, springing into its resting state so the pile visibly "settles" when the scroll stops.
const SHRINK_PER_LAYER = 0.035; // scale removed per card stacked on top
const MIN_SCALE = 0.88;
const DIM_PER_LAYER = 0.08; // ink veil opacity added per layer
const MAX_DIM = 0.26;
const SPRING = { stiffness: 150, damping: 26, mass: 0.5 };

/** "Cómo generamos impacto en cada compra" + the stackable (sticky) solution cards. */
type CardVariant = 'classic' | 'feature';

export function Solutions() {
	const { t } = useLingui();
	const reduce = useReducedMotion() ?? false;
	const stackRef = useRef<HTMLDivElement>(null);
	// Concept toggle (for the comms team) — flip every card between the classic split
	// layout and the experimental full-bleed photo layout.
	const [variant, setVariant] = useState<CardVariant>('feature');

	// Progress across the whole stacking scroll: 0 when the deck's top reaches the top of the
	// viewport, 1 once it has fully scrolled past. Each card derives its depth from this.
	const { scrollYProgress } = useScroll({ target: stackRef, offset: ['start start', 'end start'] });

	const solutions: Solution[] = [
		{
			badge: '1',
			chip: t`Physical stores`,
			title: t`Charity Rounding Up`,
			description: t`The customer adds a few cents to their card purchase and donates them to a social cause. Those cents add up and go to the solidarity project your company chooses.`,
			photo: t`A card terminal asking the customer to round up their purchase`,
			src: '/redesign/solution-pos.jpg',
		},
		{
			badge: '2',
			chip: t`eCommerce`,
			title: t`Solidarity Euro`,
			description: t`A fixed microdonation or round-up one click before checkout. Without interrupting the flow, frictionless.`,
			photo: t`An online checkout offering the Solidarity Euro before payment`,
			src: '/redesign/solution-ecommerce.jpg',
		},
		{
			badge: '3',
			chip: t`Payroll & invoices rounding`,
			title: t`Payroll & invoices`,
			description: t`Automated recurring donations through the cents of salaries or monthly subscriptions.`,
			photo: t`Hands with a receipt and calculator`,
			src: '/redesign/solution-payroll.jpg',
		},
		{
			badge: '4',
			chip: t`Solidarity Portal`,
			title: t`Solidarity Portal`,
			description: t`A tailor-made platform so your employees can join corporate or emergency campaigns.`,
			photo: t`Worldcoo dashboard`,
			src: '/redesign/solution-dashboard.png',
		},
	];

	return (
		<section className="mx-auto max-w-[1440px] px-6 pt-8 pb-16 md:pb-24 xl:px-10">
			{/* Section intro — scrolls away as usual. Keeping it out of the sticky context is what makes
			    the stack locale-proof: the cards no longer have to guess a header height that changes
			    when the title wraps to two lines in some languages. */}
			<div className="mx-auto mb-10 flex max-w-4xl flex-col items-center gap-4 text-center md:mb-14">
				<Eyebrow>{t`Our solutions`}</Eyebrow>
				<h2 className="text-3xl font-medium tracking-tight md:text-5xl">
					{t`How we generate`} <Squiggle>{t`impact`}</Squiggle> {t`on every purchase`}
				</h2>
				<p className="text-ink/70 text-base md:text-lg">
					{t`We offer tailor-made tech solutions that integrate into your company's purchase flow, frictionless and fully transparent.`}
				</p>

				<VariantToggle value={variant} onChange={setVariant} classicLabel={t`Split`} featureLabel={t`Full photo`} />
			</div>

			<div ref={stackRef} className="flex flex-col gap-4 md:gap-8">
				{solutions.map((s, i) => (
					<StackCard
						key={s.badge}
						solution={s}
						index={i}
						count={solutions.length}
						progress={scrollYProgress}
						reduce={reduce}
						variant={variant}
					/>
				))}
				{/* Settle zone — trailing scroll room (desktop only) so the last card locks onto the
				    pile and the whole assembled deck holds a beat, then releases and scrolls away
				    together. Without it the top card — being lowest in the flow — is the first one the
				    container's rising bottom edge drags off, so it appears to pass over the rest. */}
				<div aria-hidden className="hidden md:block md:h-[18rem]" />
			</div>
		</section>
	);
}

/** Segmented control to flip every card between the two concept layouts. */
function VariantToggle(props: {
	value: CardVariant;
	onChange: (v: CardVariant) => void;
	classicLabel: string;
	featureLabel: string;
}) {
	const options: { key: CardVariant; label: string }[] = [
		{ key: 'classic', label: props.classicLabel },
		{ key: 'feature', label: props.featureLabel },
	];
	return (
		<div className="border-ink/10 mt-2 inline-flex items-center gap-1 rounded-full border bg-white p-1 shadow-sm">
			{options.map((o) => {
				const active = props.value === o.key;
				return (
					<button
						key={o.key}
						type="button"
						aria-pressed={active}
						onClick={() => props.onChange(o.key)}
						className={cn(
							'rounded-full px-4 py-1.5 text-sm font-medium transition-colors',
							active ? 'bg-ink text-white' : 'text-ink/60 hover:text-ink',
						)}
					>
						{o.label}
					</button>
				);
			})}
		</div>
	);
}

function StackCard(props: {
	solution: Solution;
	index: number;
	count: number;
	progress: MotionValue<number>;
	reduce: boolean;
	variant: CardVariant;
}) {
	const { t } = useLingui();
	const s = props.solution;
	const top = `${STACK_TOP + props.index * TAB}rem`;

	// How many cards are stacked on top of this one right now (0 while it is the front card).
	const layers = useTransform(props.progress, (p) => Math.max(0, p * props.count - props.index));
	const scale = useSpring(
		useTransform(layers, (d) => Math.max(MIN_SCALE, 1 - d * SHRINK_PER_LAYER)),
		SPRING,
	);
	const dim = useSpring(
		useTransform(layers, (d) => Math.min(MAX_DIM, d * DIM_PER_LAYER)),
		SPRING,
	);

	// Ink veil — darkens the card as it is buried deeper, reinforcing the depth of the pile.
	const veil = !props.reduce && (
		<motion.div aria-hidden className="bg-ink pointer-events-none absolute inset-0" style={{ opacity: dim }} />
	);

	// Experimental full-bleed layout (concept for the comms team): the photo IS the card,
	// with the copy overlaid on a legibility gradient and glassy chrome.
	if (props.variant === 'feature') {
		return (
			<motion.div className="md:sticky" style={props.reduce ? { top } : { top, scale, transformOrigin: 'center top' }}>
				<FeatureCard solution={s}>{veil}</FeatureCard>
			</motion.div>
		);
	}

	return (
		<motion.div className="md:sticky" style={props.reduce ? { top } : { top, scale, transformOrigin: 'center top' }}>
			<article className="bg-peach text-ink relative flex flex-col overflow-hidden rounded-3xl shadow-[0_-14px_40px_-16px_rgba(37,48,44,0.22)] md:h-[27rem] lg:h-[30rem]">
				<div className="border-ink/10 flex h-16 shrink-0 items-center gap-2.5 border-b px-8 md:px-12">
					<span className="bg-brand grid size-6 shrink-0 place-items-center rounded-md text-sm font-semibold text-black tabular-nums">
						{s.badge}
					</span>
					<span className="text-ink/70 text-sm font-medium">{s.chip}</span>
				</div>

				<div className="grid grow md:grid-cols-2">
					<div className="order-2 flex flex-col justify-center p-8 md:order-1 md:p-12">
						<h3 className="text-3xl font-semibold md:text-4xl">{s.title}</h3>
						<p className="text-ink/70 mt-4 max-w-md">{s.description}</p>
						<Link
							to="/new/contact"
							className="hover:text-brand-strong mt-8 flex w-fit items-center gap-1.5 text-sm font-medium transition-colors"
						>
							{t`Contact us`}
							<ArrowUpRight className="size-4" />
						</Link>
					</div>
					<div className="relative order-1 min-h-56 md:order-2 md:min-h-0 md:overflow-hidden">
						<Image
							src={s.src}
							alt={s.photo}
							height={520}
							aspectRatio={4 / 3}
							objectFit="cover"
							className="absolute inset-0 h-full w-full object-cover!"
						/>
					</div>
				</div>

				{veil}
			</article>
		</motion.div>
	);
}

/**
 * Experimental full-bleed card. The photo fills the whole card and the copy floats on
 * top of a directional legibility gradient, with a frosted badge pill and a solid CTA —
 * a more modern, editorial take than the split text/photo cards.
 */
function FeatureCard(props: { solution: Solution; children?: ReactNode }) {
	// `useLingui()` must be called here: the `t` macro only transforms `t`...`` when `t`
	// comes from the hook in this component (a passed-in `t` renders empty).
	const { t } = useLingui();
	const s = props.solution;
	return (
		<article className="group relative flex flex-col overflow-hidden rounded-3xl text-white shadow-[0_-14px_40px_-16px_rgba(37,48,44,0.22)] md:h-[27rem] lg:h-[30rem]">
			<Image
				src={s.src}
				alt={s.photo}
				height={720}
				aspectRatio={16 / 9}
				objectFit="cover"
				className="absolute inset-0 h-full w-full scale-105 object-cover! transition-transform duration-700 ease-out group-hover:scale-110"
			/>
			<div aria-hidden className="from-ink/95 via-ink/55 absolute inset-0 bg-gradient-to-t to-transparent" />
			<div aria-hidden className="from-ink/70 absolute inset-0 bg-gradient-to-r to-transparent md:to-40%" />

			<div className="relative flex h-full flex-col">
				{/* Badge tab — fixed h-16 (= TAB) so it lines up exactly with the peeking strip
				    when cards stack; otherwise the pill sits too low and gets clipped. */}
				<div className="flex h-16 shrink-0 items-center gap-2.5 px-8 md:px-12">
					<span className="bg-brand grid size-6 shrink-0 place-items-center rounded-md text-sm font-semibold text-black tabular-nums">
						{s.badge}
					</span>
					<span className="rounded-full bg-white/10 px-3 py-1 text-sm font-medium text-white/90 ring-1 ring-white/20 backdrop-blur-md">
						{s.chip}
					</span>
				</div>

				<div className="mt-auto max-w-xl px-8 pb-8 md:px-12 md:pb-12">
					<h3 className="text-3xl font-semibold tracking-tight text-balance md:text-5xl">{s.title}</h3>
					<p className="mt-4 max-w-md text-white/85 md:text-lg">{s.description}</p>
					<Link
						to="/new/contact"
						className="text-ink mt-8 flex w-fit items-center gap-2 rounded-full bg-white py-2.5 pr-2.5 pl-5 font-medium shadow-lg transition-colors hover:bg-white/90"
					>
						{t`Contact us`}
						<span className="bg-brand grid size-6 place-items-center rounded-full text-black">
							<ArrowUpRight className="size-4" />
						</span>
					</Link>
				</div>
			</div>

			{props.children}
		</article>
	);
}
